import type { SessionUsage, Profile, DailyUsage } from '../types/profile.js';

let currentSessionId: string | null = null;

export function initSession(): string {
    currentSessionId = `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return currentSessionId;
}

export function getSessionId(): string {
    if (!currentSessionId) {
        currentSessionId = initSession();
    }
    return currentSessionId;
}

export function getTodayDateString(): string {
    return new Date().toISOString().split('T')[0];
}

export function calculateRemainingTime(profile: Profile, usage: SessionUsage | null | undefined): number {
    const limitSeconds = profile.sessionTimeLimitMinutes * 60;
    const watchedSeconds = usage?.watchedSeconds ?? 0;
    return Math.max(0, limitSeconds - watchedSeconds);
}

export function isTimeExhausted(profile: Profile, usage: SessionUsage | null | undefined): boolean {
    return calculateRemainingTime(profile, usage) <= 0;
}

export function formatTimeRemaining(seconds: number): string {
    if (seconds <= 0) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

export function updateSessionUsage(existingUsage: SessionUsage | null | undefined, profileId: string, additionalSeconds: number): SessionUsage {
    const sessionId = getSessionId();
    const now = new Date().toISOString();

    // If same session, add to existing
    if (existingUsage && existingUsage.sessionId === sessionId) {
        return {
            ...existingUsage,
            watchedSeconds: existingUsage.watchedSeconds + additionalSeconds,
            lastUpdated: now
        };
    }

    // New session - reset counter
    return {
        profileId,
        sessionId,
        watchedSeconds: additionalSeconds,
        startedAt: now,
        lastUpdated: now
    };
}

// Backward compatibility wrapper
export function updateDailyUsage(existingUsage: DailyUsage | null | undefined, profileId: string, additionalSeconds: number): DailyUsage {
    const today = getTodayDateString();
    const now = new Date().toISOString();
    if (existingUsage && existingUsage.date === today) {
        return { ...existingUsage, watchedSeconds: existingUsage.watchedSeconds + additionalSeconds, lastUpdated: now };
    }
    return { profileId, date: today, watchedSeconds: additionalSeconds, lastUpdated: now };
}

export interface TimeExtensionOption {
    id: string;
    labelKey: string;
    minutes: number;
}

export const TIME_EXTENSION_OPTIONS: TimeExtensionOption[] = [
    { id: '15min', labelKey: 'extend15Minutes', minutes: 15 },
    { id: '30min', labelKey: 'extend30Minutes', minutes: 30 },
    { id: '60min', labelKey: 'extend60Minutes', minutes: 60 },
    { id: 'unlimited', labelKey: 'unlockUntilSessionEnd', minutes: -1 },
];

export function applyTimeExtension(usage: SessionUsage, _profile: Profile, extensionMinutes: number): SessionUsage {
    if (extensionMinutes === -1) {
        // Reset to 0 for unlimited session
        return { ...usage, watchedSeconds: 0, lastUpdated: new Date().toISOString() };
    }
    const extensionSeconds = extensionMinutes * 60;
    return {
        ...usage,
        watchedSeconds: Math.max(0, usage.watchedSeconds - extensionSeconds),
        lastUpdated: new Date().toISOString()
    };
}
